// src/features/chat/panel/PanelAttachmentsView.tsx
// The panel's Media / Files / Links views — everything the loaded
// conversation carries, each row jumping back to its source message in the
// stream. Client-side aggregation (same as ChatDetailPage's
// AttachmentsPanel): walks local message state, no extra RPCs.

import { useEffect, useState } from 'react';
import { useI18n } from '@react/hooks';
import {
  formatFileSize,
  getFileExtension,
  isImageAttachment,
} from '@/features/chat/logic';
import type { AttachmentPreview } from '@core/types/chat';
import { extractLinks, linkDomain, messageId, messageTs } from './panelShared';

export type PanelView = 'stream' | 'media' | 'files' | 'links';

interface Flat {
  preview: AttachmentPreview;
  messageId: string;
  ts: number;
}

function MediaThumb({
  preview,
  resolveUrl,
  onOpen,
}: {
  preview: AttachmentPreview;
  resolveUrl: (attachmentId: string) => Promise<string>;
  onOpen: () => void;
}) {
  const [url, setUrl] = useState(preview.thumbnail_url ?? '');
  const [loading, setLoading] = useState(!preview.thumbnail_url);

  useEffect(() => {
    if (!preview.thumbnail_url && preview.attachment?.id) {
      let cancelled = false;
      resolveUrl(preview.attachment.id)
        .then((u) => { if (!cancelled) { setUrl(u); setLoading(false); } })
        .catch(() => { if (!cancelled) setLoading(false); });
      return () => { cancelled = true; };
    }
  }, [preview.thumbnail_url, preview.attachment?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return <div className="aspect-square animate-pulse rounded-md bg-muted" aria-hidden="true" />;
  }
  return (
    <button
      type="button"
      onClick={onOpen}
      className="group aspect-square overflow-hidden rounded-md border border-border bg-muted/30 transition-all hover:ring-2 hover:ring-ring focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring motion-reduce:transition-none"
      title={preview.file_name}
    >
      <img src={url} alt={preview.file_name} className="h-full w-full object-cover" loading="lazy" />
    </button>
  );
}

export function PanelAttachmentsView({
  view,
  messages,
  resolveUrl,
  onOpenImage,
  onDownload,
  onJump,
}: {
  view: Exclude<PanelView, 'stream'>;
  messages: any[];
  resolveUrl: (attachmentId: string) => Promise<string>;
  onOpenImage: (attachmentId: string) => void;
  onDownload: (attachmentId: string, fileName: string) => void;
  /** Scroll the stream to the source message (no-op when it isn't loaded). */
  onJump: (messageId: string) => void;
}) {
  const { t, locale } = useI18n();

  const flat: Flat[] = [];
  for (const m of messages) {
    const mid = messageId(m);
    if (!mid) continue;
    for (const p of m?.attachment_previews?.items ?? []) {
      flat.push({ preview: p, messageId: mid, ts: messageTs(m) });
    }
  }
  flat.sort((a, b) => b.ts - a.ts);
  const images = flat.filter((f) => isImageAttachment(f.preview));
  const files = flat.filter((f) => !isImageAttachment(f.preview));

  if (view === 'media') {
    if (images.length === 0) {
      return <p className="py-8 text-center text-sm text-muted-foreground">{t('chat.entityPanel.noMedia')}</p>;
    }
    return (
      <div className="grid grid-cols-3 gap-1.5 p-1">
        {images.map((f, idx) => (
          <MediaThumb
            key={`${f.messageId}-${f.preview.attachment?.id ?? idx}`}
            preview={f.preview}
            resolveUrl={resolveUrl}
            onOpen={() => onOpenImage(f.preview.attachment?.id ?? '')}
          />
        ))}
      </div>
    );
  }

  if (view === 'files') {
    if (files.length === 0) {
      return <p className="py-8 text-center text-sm text-muted-foreground">{t('chat.entityPanel.noFiles')}</p>;
    }
    return (
      <ul className="space-y-1 p-1">
        {files.map((f, idx) => {
          const aid = f.preview.attachment?.id ?? '';
          return (
            <li key={`${f.messageId}-${aid || idx}`} className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => onDownload(aid, f.preview.file_name)}
                className="flex min-w-0 flex-1 items-center gap-2 rounded-md px-2 py-1.5 text-left transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                title={f.preview.file_name}
              >
                <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded bg-primary/10 text-[10px] font-bold text-primary">
                  {getFileExtension(f.preview.file_name) || '?'}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-xs font-medium text-foreground">{f.preview.file_name}</p>
                  <p className="text-[10px] tabular-nums text-muted-foreground">
                    {formatFileSize(f.preview.size_bytes)}
                    {f.ts > 0 && <> · {new Intl.DateTimeFormat(locale, { dateStyle: 'medium' }).format(new Date(f.ts))}</>}
                  </p>
                </div>
              </button>
              <button
                type="button"
                onClick={() => onJump(f.messageId)}
                className="shrink-0 rounded p-1 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                aria-label={t('chat.entityPanel.jumpToMessage')}
                title={t('chat.entityPanel.jumpToMessage')}
              >
                <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6" />
                  <path d="M15 3h6v6" />
                  <path d="M10 14L21 3" />
                </svg>
              </button>
            </li>
          );
        })}
      </ul>
    );
  }

  // Links
  const links: { url: string; messageId: string; snippet: string; ts: number }[] = [];
  for (const m of messages) {
    const mid = messageId(m);
    if (!mid) continue;
    for (const url of extractLinks(m?.content ?? '')) {
      const snippet = (m?.content ?? '').replace(url, '').trim().slice(0, 80);
      links.push({ url, messageId: mid, snippet, ts: messageTs(m) });
    }
  }
  links.sort((a, b) => b.ts - a.ts);
  if (links.length === 0) {
    return <p className="py-8 text-center text-sm text-muted-foreground">{t('chat.entityPanel.noLinks')}</p>;
  }
  return (
    <ul className="space-y-1 p-1">
      {links.map((l, idx) => (
        <li key={`${l.messageId}-${idx}`}>
          <button
            type="button"
            onClick={() => onJump(l.messageId)}
            className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <div className="min-w-0 flex-1">
              <p className="truncate text-xs font-medium text-foreground">{linkDomain(l.url)}</p>
              {l.snippet && (
                <p className="truncate text-[11px] text-muted-foreground">{l.snippet}</p>
              )}
            </div>
            <span className="shrink-0 text-[10px] tabular-nums text-muted-foreground">
              {l.ts > 0 ? new Intl.DateTimeFormat(locale, { dateStyle: 'medium' }).format(new Date(l.ts)) : ''}
            </span>
          </button>
        </li>
      ))}
    </ul>
  );
}
